import { useState, useCallback, useRef } from 'react';
import { useApiService } from '@/lib/api';

interface Subject {
  id: string;
  display_name: string;
  name: string;
  description: string;
  classId: string;
  createdAt: string;
  updatedAt: string;
}

interface SubjectCache {
  subjects: Subject[];
  hasLoaded: boolean;
  lastFetchTime: number;
  classId: string;
}

// Global cache that persists across component re-mounts
let globalSubjectCache: SubjectCache = {
  subjects: [],
  hasLoaded: false,
  lastFetchTime: 0,
  classId: ''
};

// Cache duration: 10 minutes
const CACHE_DURATION = 10 * 60 * 1000;

export const useSubjectCache = (classId: string = '9') => {
  const [cache, setCache] = useState<SubjectCache>(globalSubjectCache);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiService = useApiService();
  const fetchPromiseRef = useRef<Promise<any> | null>(null);

  // Check if cache is still valid for the same class
  const isCacheValid = useCallback(() => {
    const now = Date.now();
    return globalSubjectCache.hasLoaded && 
           globalSubjectCache.classId === classId &&
           (now - globalSubjectCache.lastFetchTime) < CACHE_DURATION;
  }, [classId]);

  // Update global cache and local state
  const updateCache = useCallback((newCache: Partial<SubjectCache>) => {
    globalSubjectCache = { ...globalSubjectCache, ...newCache };
    setCache(globalSubjectCache);
  }, []);

  const fetchSubjects = useCallback(async (retryCount = 0) => {
    // Return cached data if valid
    if (isCacheValid()) {
      return globalSubjectCache.subjects;
    }

    // Prevent duplicate API calls
    if (fetchPromiseRef.current) {
      return await fetchPromiseRef.current;
    }

    const maxRetries = 3;

    try {
      setIsLoading(true);
      setError(null);

      const fetchPromise = apiService.get<{
        success: boolean;
        data: Subject[];
      }>(`/classes/${classId}/subjects`);
      
      fetchPromiseRef.current = fetchPromise;
      const response = await fetchPromise;

      if (response.success) {
        const subjectData = response.data || [];
        
        updateCache({
          subjects: subjectData,
          hasLoaded: true,
          lastFetchTime: Date.now(),
          classId: classId
        });

        console.log(`✅ Loaded ${subjectData.length} subjects for class ${classId} (cached)`);
        return subjectData;
      } else {
        throw new Error('API returned unsuccessful response');
      }
    } catch (error: any) {
      console.error('Error fetching subjects:', error);
      setError('Failed to load subjects');
      
      // Don't retry on authentication/authorization errors (4xx)
      if (error.status >= 400 && error.status < 500) {
        console.error(`Authentication/client error (${error.status}): ${error.message}`);
        throw error;
      }
      
      // Only retry on server errors (5xx) and network errors
      if (retryCount < maxRetries) {
        console.log(`Retrying subjects API call after error (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
        return fetchSubjects(retryCount + 1);
      }
      
      throw error;
    } finally {
      setIsLoading(false);
      fetchPromiseRef.current = null;
    }
  }, [apiService, classId, isCacheValid, updateCache]);

  const invalidateCache = useCallback(() => {
    updateCache({
      subjects: [],
      hasLoaded: false,
      lastFetchTime: 0,
      classId: ''
    });
  }, [updateCache]);

  return {
    subjects: cache.subjects,
    hasLoaded: cache.hasLoaded,
    isLoading,
    error,
    fetchSubjects,
    invalidateCache,
    isCacheValid: isCacheValid()
  };
};
